import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export type DashboardFailureRequest = {
  id: string;
  heading: string;
  message: string;
  ariaLabel: string;
  onClose?: () => void;
  children?: React.ReactNode;
};

type DashboardFailureModalProps = {
  heading: string;
  message: string;
  ariaLabel: string;
  onClose: () => void;
  children?: React.ReactNode;
};

/**
 * A small dialog pinned to the top of the screen. It neither blocks the page
 * nor takes focus, so work continues underneath until it is closed.
 */
const DashboardFailureModal: React.FC<DashboardFailureModalProps> = ({
  heading,
  message,
  ariaLabel,
  onClose,
  children,
}) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const dialog = (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={ariaLabel}
      aria-live="assertive"
      className="fixed inset-x-3 top-3 z-[200] mx-auto max-w-md rounded-xl border border-rule-strong bg-paper p-4 text-ink shadow-[0_18px_55px_var(--ib-shadow)]"
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink-strong">{heading}</p>
          <p className="mt-1 whitespace-pre-line break-words text-xs leading-5 text-ink-muted">{message}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 py-0.5 text-[10px] uppercase tracking-[0.2em] text-ink-muted transition-colors hover:text-ink"
        >
          Close
        </button>
      </div>
      {children ? <div className="mt-3 flex items-center justify-end gap-4">{children}</div> : null}
    </div>
  );

  return typeof document === 'undefined' ? dialog : createPortal(dialog, document.body);
};

export default DashboardFailureModal;
